// packages/web/src/lib/store/save-slice.ts
/**
 * Save slice — global sync status indicator (5-state machine).
 *
 * 19BN Phase 3: drives the sidebar save-status-row. Reuses the SaveStatus
 * concept from editable-text.tsx but lifts it to a global Zustand slice so
 * any IDB write can trigger the sync pipeline.
 *
 * States: idle → pending → saving → saved/error → idle
 *
 * Spec: docs/architecture/account-system-design.md §3.3, §9.3.4
 */

import type { StateCreator } from 'zustand';
import type { AppStore } from './index.js';

export type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

export interface SaveSlice {
  saveStatus: SaveStatus;
  lastSavedAt: number | null;
  pendingCount: number;
  isOnline: boolean;
  markPending: () => void;
  startSaving: () => void;
  markSaved: () => void;
  markError: () => void;
  setOnline: (online: boolean) => void;
  setPendingCount: (n: number) => void;
}

let savedResetTimer: ReturnType<typeof setTimeout> | null = null;

export const createSaveSlice: StateCreator<AppStore, [], [], SaveSlice> = (set) => ({
  saveStatus: 'idle',
  lastSavedAt: null,
  pendingCount: 0,
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,

  markPending: () => {
    if (savedResetTimer) { clearTimeout(savedResetTimer); savedResetTimer = null; }
    set((s) => ({ saveStatus: s.saveStatus === 'saving' ? 'saving' : 'pending' }));
  },

  startSaving: () => {
    if (savedResetTimer) { clearTimeout(savedResetTimer); savedResetTimer = null; }
    set({ saveStatus: 'saving' });
  },

  markSaved: () => {
    const now = Date.now();
    set({ saveStatus: 'saved', lastSavedAt: now, pendingCount: 0 });
    // Auto-revert to idle after 1.2s (like EditableText's SaveIndicator).
    if (savedResetTimer) clearTimeout(savedResetTimer);
    savedResetTimer = setTimeout(() => set({ saveStatus: 'idle' }), 1200);
  },

  markError: () => {
    if (savedResetTimer) { clearTimeout(savedResetTimer); savedResetTimer = null; }
    set({ saveStatus: 'error' });
  },

  setOnline: (online) => set({ isOnline: online }),
  setPendingCount: (n) => set({ pendingCount: n }),
});
